import {
  RiBookOpenLine,
  RiCoinsLine,
  RiFunctionLine,
  RiNotification3Line,
  RiStackLine,
  RiTableLine,
} from "@remixicon/react";
import type { ReactNode } from "react";
import type { ScreenMode } from "@/density/derive";
import { hashOfDensityView, hashOfScreen, type Screen } from "@/hooks/use-screen";

/** Пункт боковой панели: знает про экраны клиента, поэтому живёт в приложении, а не в UIKit. */
export interface NavItem {
  /** Уникален на всю панель: у видов `view:<вид>`, у экранов `screen:<экран>`. */
  key: string;
  label: string;
  icon: ReactNode;
  href: string;
  current: boolean;
}

/** Группа пунктов с подписью: подпись — заголовок группы, а не ссылка (§111 п. 3). */
export interface NavGroup {
  /** Стабильное имя группы, уникальное на панель: React-ключ группы в панели. Id заголовка строит сам экран. */
  key: string;
  label: string;
  items: NavItem[];
}

export interface NavGroups {
  /** Четыре вида экрана плотностей. */
  views: NavGroup;
  /** Экраны «Алерты» и «Библиотека». */
  screens: NavGroup;
}

/** Подписи групп панели (§111 п. 3). */
export const NAV_GROUP_LABELS = {
  views: "Плотности",
  screens: "Инструменты",
} as const;

/**
 * Подписи видов экрана плотностей (§9 1.21, §61): одно место определения для
 * пунктов панели и для названия экрана в верхней полосе.
 */
export const DENSITY_VIEW_LABELS: Record<ScreenMode, string> = {
  cards: "Карточки",
  coins: "По монете",
  densities: "Таблица",
  situations: "Ситуации",
};

/**
 * Подписи экранов «Алерты», «Библиотека» и «Настройки»: для пунктов панели
 * и названия экрана в полосе. Пункта панели у «Настроек» нет — вход из меню профиля.
 */
export const SCREEN_LABELS: Record<Exclude<Screen, "densities">, string> = {
  alerts: "Алерты",
  library: "Библиотека",
  settings: "Настройки",
};

/** Значки видов — по макету §111 п. 4: сетка, монеты, таблица с линиями, стопка. */
const VIEW_ICONS: Record<ScreenMode, ReactNode> = {
  cards: <RiFunctionLine />,
  coins: <RiCoinsLine />,
  densities: <RiTableLine />,
  situations: <RiStackLine />,
};

/** Порядок видов §61 п. 1. */
const VIEW_ORDER: readonly ScreenMode[] = ["cards", "coins", "densities", "situations"];

/**
 * Пункты панели §9 (1.43, 1.73, §86): плоский список из шести пунктов в двух
 * группах. Вынесены из каркаса, чтобы состав проверялся без сессии, потока и
 * сети.
 *
 * `view` — вид из адреса; `null`, пока открыт другой экран. У вида `current` —
 * открыт экран плотностей и вид совпал. «Карточки» текущие и на корне `#/`:
 * разбор адреса отдаёт карточки для него же.
 */
export function buildNavItems(screen: Screen, view: ScreenMode | null): NavGroups {
  return {
    views: {
      key: "group:views",
      label: NAV_GROUP_LABELS.views,
      items: VIEW_ORDER.map((mode) => ({
        key: `view:${mode}`,
        label: DENSITY_VIEW_LABELS[mode],
        icon: VIEW_ICONS[mode],
        href: hashOfDensityView(mode),
        current: screen === "densities" && view === mode,
      })),
    },
    screens: {
      key: "group:screens",
      label: NAV_GROUP_LABELS.screens,
      items: [
        {
          key: "screen:alerts",
          label: SCREEN_LABELS.alerts,
          icon: <RiNotification3Line />,
          href: hashOfScreen("alerts"),
          current: screen === "alerts",
        },
        {
          key: "screen:library",
          label: SCREEN_LABELS.library,
          icon: <RiBookOpenLine />,
          href: hashOfScreen("library"),
          current: screen === "library",
        },
      ],
    },
  };
}
